import {
  ChangeDetectorRef,
  Directive,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { isObservable, merge, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, mergeAll, takeUntil } from 'rxjs/operators';

/** Purpose is to help Angular understand that "there is" some change
 * and it needs to run changeDetection cycle.
 * !Use case! you have selectors from store and *ngIf. Angular would not render anything
 * !Use case! if you're using `changeDetection: ChangeDetectionStrategy.OnPush,` for your component
 *
 * Another way is to have:
 * 1. "Container" or "Live" component and pass values to "real" one via `Input`s, but you would
 * have to have changeDetection triggered by second point anyway
 * 2. have something that triggers ChangeDetection: delays, httpRequest, user click etc.
 */
@Directive({
  selector: '[atxAngularReactive]',
  exportAs: '[atxAngularReactive]',
  standalone: false,
})
// Should be abandond if we or Angular goes zoneless and they handle it
export class AngularReactive implements OnInit, OnDestroy {
  @Input('atxAngularReactive')
  public runCdOnChange$?: Observable<unknown>[];
  private destroyed$$ = new Subject<void>();

  constructor(private cdr: ChangeDetectorRef) {}

  public ngOnInit() {
    let allStreams$: Observable<unknown>[] = [];

    if (this.runCdOnChange$) {
      allStreams$ = this.runCdOnChange$;
    }

    if (
      !Array.isArray(allStreams$) ||
      !allStreams$.some((stream) => isObservable(stream))
    ) {
      throw new Error('Input interface is Array of Observables');
    }

    merge(allStreams$.map((stream) => stream.pipe(distinctUntilChanged())))
      .pipe(mergeAll(), takeUntil(this.destroyed$$))
      .subscribe(() => {
        // this thing below does the trick, it triggers ChangeDetection
        // so your inputs and components can be updated
        this.cdr.detectChanges(); // `.detectChanges` could be anywhere -
        // here or in Pipe exactly how NGRX team does
        // https://github.com/ngrx/platform/blob/abcc5992d1545217703c78deb5c885c97361a432/modules/component/src/push/push.pipe.ts#L87
        // however, we cannot do it do to the fact when Angular has zone, ngrx team does not call `.detectChanges`
        // https://github.com/ngrx/platform/blob/abcc5992d1545217703c78deb5c885c97361a432/modules/component/src/core/cd-aware/creator_render.ts#L12
      });
  }

  public ngOnDestroy() {
    this.destroyed$$.next();
    this.destroyed$$.complete();
  }
}
